import { createAsyncThunk, ThunkDispatch, GetThunkAPI, UnknownAction } from '@reduxjs/toolkit'
import { handleUIError } from 'app/services/client/error-handling'
import { RootState } from '../redux/Store'
import { ApiError, AppErrorType } from 'app/services/common-error-utils'
import { t } from 'i18next'

const buildNotification = (error: ApiError | any) => ({
  type: error?.type,
  name: error?.name || error?.constructor?.name || t('errors.errorTitle'),
  message: error?.message || `Operation failed: ${error}`,
  status: error?.status || 500,
  stack: error?.stack || 'No stack trace available',
  timestamp: new Date().toISOString(),
  clientReqId: error?.clientReqId,
  cause: error?.cause
})

export const withThunkHandler =
  <TArgs = any, TResult = any>(
    handler: (thunkAPI: any, args: TArgs) => Promise<TResult>,
    isShowErrorNotification: boolean = false
  ) =>
  async (args: TArgs, thunkAPI: any): Promise<TResult> => {
    try {
      return await handler(thunkAPI, args)
    } catch (error: ApiError | any) {
      const notification = buildNotification(error)
      const isInvalidSession = error instanceof ApiError && error.type === AppErrorType.INVALID_SESSION

      if (thunkAPI?.dispatch && (isInvalidSession || isShowErrorNotification)) {
        handleUIError(thunkAPI.dispatch, notification)
        return Promise.reject(error)
      }

      return thunkAPI.rejectWithValue(notification)
    }
  }

export type AsyncThunkAPI = GetThunkAPI<{
  state: RootState
  dispatch?: ThunkDispatch<unknown, unknown, UnknownAction> | undefined
  extra?: unknown
  rejectValue?: unknown
  serializedErrorType?: unknown
  pendingMeta?: unknown
  fulfilledMeta?: unknown
  rejectedMeta?: unknown
}>

export const wrapCreateAsyncThunk = <TReturned = void, TArg = void>(
  typePrefix: string,
  payloadCreator: (arg: TArg, thunkAPI: AsyncThunkAPI) => Promise<TReturned>,
  onError?: (error: Record<string, any>, dispatch: ThunkDispatch<unknown, unknown, UnknownAction>) => void,
  showErrorNotification: boolean = true
) =>
  createAsyncThunk<TReturned, TArg, { state: RootState }>(typePrefix, async (arg, thunkAPI) => {
    try {
      return await payloadCreator(arg, thunkAPI)
    } catch (err: ApiError | any) {
      const notification = buildNotification(err)

      if (thunkAPI?.dispatch) {
        handleUIError(thunkAPI.dispatch, notification, showErrorNotification)
      }

      if (onError && thunkAPI?.dispatch) {
        onError(notification, thunkAPI.dispatch)
      }

      return thunkAPI.rejectWithValue(notification)
    }
  })
